import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const Navbar = () => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const isDynamicPage =
    location.pathname === '/' || location.pathname.startsWith('/projects/');

  const isFixedUrbanPage =
    location.pathname === '/projects' ||
    location.pathname === '/management' ||
    location.pathname === '/about' ||
    location.pathname === '/contact';

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);

    if (isDynamicPage) {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    } else {
      setIsScrolled(true);
    }
  }, [isDynamicPage]);

  const navLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.projects'), path: '/projects' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.contact'), path: '/contact' },
  ];

  let baseColor = '';
  if (isDynamicPage) {
    baseColor = isScrolled ? 'text-[#E0E0E0]' : 'text-[#D9D9D9]';
  } else if (isFixedUrbanPage) {
    baseColor = 'text-[#E0E0E0]';
  } else {
    baseColor = 'text-[#D9D9D9]';
  }

  const hoverColor = 'hover:text-[#C8102E]';

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 safe-area-top ${
          isScrolled
            ? 'bg-[#1F1F1F]/95 backdrop-blur-md shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div
          className={`container-custom flex justify-between items-center animate-fade-in-down ${
            isScrolled ? 'py-3' : 'py-5'
          }`}
        >
          <Link to="/" className="flex items-center">
            <img
              src="/images/LOGO.png"
              alt="MAD Logo"
              className="h-8 md:h-10 w-auto object-contain"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex space-x-6 items-center">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link transition duration-300 ${baseColor} ${hoverColor}`}
              >
                {link.name}
              </Link>
            ))}
            <LanguageSwitcher
              buttonClassName={`nav-link transition duration-300 ${baseColor} ${hoverColor}`}
            />
          </nav>

          {/* Mobile menu button */}
          <button
            className={`md:hidden ${baseColor}`}
            onClick={toggleMenu}
            aria-label={isMenuOpen ? t('common.close') : t('common.menu')}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden fixed inset-0 bg-[#1F1F1F] z-[100] flex flex-col animate-fade-in-down text-[#E0E0E0]">
          <div className="container-custom py-5 flex justify-between items-center">
            <Link to="/" className="flex items-center" onClick={() => setIsMenuOpen(false)}>
              <img
                src="/images/Logo M.png"
                alt="MAD Logo"
                className="h-8 w-auto object-contain"
              />
            </Link>
            <button
              className="text-[#AAAAAA]"
              onClick={toggleMenu}
              aria-label={t('common.close')}
            >
              <X size={24} />
            </button>
          </div>
          <nav className="flex flex-col items-center justify-center flex-grow space-y-8 pt-10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link text-xl ${baseColor} ${hoverColor} transition duration-300`}
                onClick={() => setIsMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            <LanguageSwitcher
              buttonClassName={`nav-link text-lg ${baseColor} ${hoverColor} transition duration-300`}
              menuClassName="left-1/2 -translate-x-1/2 right-auto"
            />
          </nav>
        </div>
      )}
    </>
  );
};

export default Navbar;
